import { api } from './client';

export interface ContextualSnapshot {
  activity: string;
  detectedIntent: string;
  relatedTopics: string[];
  timeContext: string;
  locationContext?: string;
  productivityState: string;
  tabsConsidered: number;
  generatedAt: string;
}

export interface ContextualSuggestion {
  id: string;
  type: 'link' | 'insight' | 'action' | 'resource';
  title: string;
  description: string;
  confidence: number;
  icon: string;
  tags: string[];
}

export interface ContextualLearningStats {
  snapshotCount: number;
  suggestionCount: number;
  feedbackCount: number;
  acceptanceRate: number;
  firstSnapshotAt?: string;
  lastSuggestionAt?: string;
}

interface RawSnapshot {
  activity?: string;
  detected_intent?: string;
  related_topics?: string[];
  time_context?: string;
  location_context?: string;
  productivity_state?: string;
  tabs_considered?: number;
  generated_at?: string;
}

interface RawLearningStats {
  snapshot_count?: number;
  suggestion_count?: number;
  feedback_count?: number;
  acceptance_rate?: number;
  first_snapshot_at?: string;
  last_suggestion_at?: string;
}

interface RawSuggestion {
  id?: string;
  type?: string;
  title?: string;
  description?: string;
  confidence?: number;
  icon?: string;
  tags?: string[];
}

function mapSnapshot(raw: RawSnapshot | null | undefined): ContextualSnapshot {
  return {
    activity: String(raw?.activity ?? 'General Browsing'),
    detectedIntent: String(raw?.detected_intent ?? ''),
    relatedTopics: Array.isArray(raw?.related_topics) ? raw!.related_topics!.map((t) => String(t)) : [],
    timeContext: String(raw?.time_context ?? ''),
    locationContext: raw?.location_context ? String(raw.location_context) : undefined,
    productivityState: String(raw?.productivity_state ?? 'neutral'),
    tabsConsidered: Number(raw?.tabs_considered ?? 0),
    generatedAt: String(raw?.generated_at ?? ''),
  };
}

function mapStats(raw: RawLearningStats | null | undefined): ContextualLearningStats {
  return {
    snapshotCount: Number(raw?.snapshot_count ?? 0),
    suggestionCount: Number(raw?.suggestion_count ?? 0),
    feedbackCount: Number(raw?.feedback_count ?? 0),
    acceptanceRate: Number(raw?.acceptance_rate ?? 0),
    firstSnapshotAt: raw?.first_snapshot_at,
    lastSuggestionAt: raw?.last_suggestion_at,
  };
}

const VALID_TYPES: ContextualSuggestion['type'][] = ['link', 'insight', 'action', 'resource'];

function mapSuggestion(raw: RawSuggestion): ContextualSuggestion {
  const t = String(raw.type ?? 'insight') as ContextualSuggestion['type'];
  return {
    id: String(raw.id ?? ''),
    type: VALID_TYPES.includes(t) ? t : 'insight',
    title: String(raw.title ?? ''),
    description: String(raw.description ?? ''),
    confidence: Math.max(0, Math.min(100, Math.round(Number(raw.confidence ?? 0)))),
    icon: String(raw.icon ?? '💡'),
    tags: Array.isArray(raw.tags) ? raw.tags.map((tag) => String(tag)) : [],
  };
}

export interface CurrentContextResponse {
  context: ContextualSnapshot;
  learningStats: ContextualLearningStats;
}

// Server-side insights payload returned by GET /contextual-intelligence/insights.
// The summary + patterns shapes match what the ProactiveIntelligenceEngine emits
// (arbitrary JSON), so we keep both as `unknown` at the type level and let the
// UI decide what to render.
export interface ContextInsightsResponse {
  summary: unknown;
  patterns: unknown;
  generated_at: string;
}

export const contextualIntelligenceService = {
  async getCurrentContext(): Promise<CurrentContextResponse> {
    const response = await api.get<{ context?: RawSnapshot; learning_stats?: RawLearningStats }>(
      '/contextual-intelligence/current',
    );
    return {
      context: mapSnapshot(response.data?.context),
      learningStats: mapStats(response.data?.learning_stats),
    };
  },

  async getSuggestions(): Promise<ContextualSuggestion[]> {
    const response = await api.get<{ suggestions?: RawSuggestion[] }>(
      '/contextual-intelligence/suggestions',
    );
    const rows = response.data?.suggestions ?? [];
    return rows.map(mapSuggestion).filter((s) => s.id !== '');
  },

  // enrichSuggestions fans out the listed suggestions to Gemini so each card's
  // title + description becomes personalized to the user's current tabs. Returned
  // suggestions preserve the input order and use the same IDs as the originals.
  async enrichSuggestions(suggestions: ContextualSuggestion[]): Promise<ContextualSuggestion[]> {
    if (suggestions.length === 0) return [];
    try {
      const response = await api.post<{ suggestions?: RawSuggestion[] }>(
        '/contextual-intelligence/enrich',
        { suggestions },
      );
      const rows = response.data?.suggestions ?? [];
      return rows.map(mapSuggestion).filter((s) => s.id !== '');
    } catch {
      // Enrichment is best-effort — fall back to the static heuristic copy.
      return suggestions;
    }
  },

  async getInsights(): Promise<ContextInsightsResponse> {
    try {
      const response = await api.get<ContextInsightsResponse>(
        '/contextual-intelligence/insights',
      );
      return response.data ?? { summary: {}, patterns: [], generated_at: new Date().toISOString() };
    } catch {
      return { summary: {}, patterns: [], generated_at: new Date().toISOString() };
    }
  },

  async submitFeedback(suggestionId: string, accepted: boolean): Promise<void> {
    await api.post('/contextual-intelligence/feedback', {
      suggestion_id: suggestionId,
      accepted,
    });
  },
};

export default contextualIntelligenceService;
